import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import vmMaintenanceService from "@/services/vmMaintenanceService";
import type { VmMaintenanceStatus, VmShutdownResponse } from "@/types";
import { extractErrorMessage } from "@/utils/errors";

/**
 * Administrator > VM Maintenance -- operational actions on the EC2 host.
 * First action: Shutdown VM (runs stop-dev.sh, then OS shutdown in 1 minute).
 * Backup and disk cleanup will be added here later. The backend re-checks
 * every guard on submit; this page only reflects them.
 */

function formatUptime(seconds: number | null): string {
  if (seconds == null) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${d ? `${d}d ` : ""}${h}h ${m}m`;
}

function Flag({ ok, okLabel, badLabel }: { ok: boolean; okLabel: string; badLabel: string }) {
  return <Chip size="small" color={ok ? "success" : "error"} label={ok ? okLabel : badLabel} />;
}

export function VmMaintenancePage() {
  const [status, setStatus] = useState<VmMaintenanceStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<VmShutdownResponse | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setStatus(await vmMaintenanceService.getStatus());
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const closeDialog = () => {
    setDialogOpen(false);
    setConfirmText("");
    setSubmitError(null);
  };

  const handleShutdown = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await vmMaintenanceService.shutdown(confirmText);
      setResult(res);
      setDialogOpen(false);
      setConfirmText("");
    } catch (err) {
      setSubmitError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const phrase = status?.confirmation_phrase ?? "SHUTDOWN VM";

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: "auto" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            VM Maintenance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Operational actions on the server. More actions (backup, disk cleanup) will be added here.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={() => void load()} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && !status && <CircularProgress size={24} />}

      {result && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Shutdown started by {result.requested_by}. {result.message} Output is logged to {result.log_file}.
        </Alert>
      )}

      {status && (
        <Stack spacing={2}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                Server
              </Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>Host</TableCell>
                    <TableCell>{status.hostname}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Uptime</TableCell>
                    <TableCell>{formatUptime(status.uptime_seconds)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Disk (/)</TableCell>
                    <TableCell>
                      {status.disk_used_gb} GB used of {status.disk_total_gb} GB ({status.disk_free_gb} GB free)
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Time (IST)</TableCell>
                    <TableCell>
                      {status.ist_time} {status.trading_day ? "(trading day)" : "(non-trading day)"}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Shutdown VM
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Runs <code>stop-dev.sh</code> (stops the app processes and the Postgres container), then shuts the
                    VM down one minute later. Start it again from the AWS console.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<PowerSettingsNewIcon />}
                  disabled={!status.shutdown_allowed || Boolean(result)}
                  onClick={() => setDialogOpen(true)}
                >
                  Shutdown VM
                </Button>
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap", rowGap: 1 }}>
                <Flag ok={!status.within_market_guard} okLabel="Outside market window" badLabel="Market window (09:00–15:35)" />
                <Flag ok={status.open_live_positions === 0} okLabel="No open LIVE positions" badLabel={`${status.open_live_positions} open LIVE position(s)`} />
                <Flag ok={status.pending_orders === 0} okLabel="No pending orders" badLabel={`${status.pending_orders} pending order(s)`} />
                <Flag ok={status.sudo_ready} okLabel="Shutdown permission configured" badLabel="Shutdown permission missing" />
                <Flag ok={status.stop_script_present} okLabel="stop-dev.sh found" badLabel="stop-dev.sh missing" />
              </Stack>

              {status.blockers.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Shutdown is blocked:
                  </Typography>
                  {status.blockers.map((b) => (
                    <Typography key={b} variant="body2">
                      • {b}
                    </Typography>
                  ))}
                </Alert>
              )}
              {!status.sudo_ready && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  One-time server setup: run <code>sudo visudo -f /etc/sudoers.d/tekinvest-shutdown</code> and add:
                  <Box component="pre" sx={{ m: 0, mt: 1, whiteSpace: "pre-wrap" }}>
                    {status.sudoers_line}
                  </Box>
                </Alert>
              )}
              {status.warnings.map((w) => (
                <Alert key={w} severity="info" sx={{ mt: 2 }}>
                  {w}
                </Alert>
              ))}
            </CardContent>
          </Card>
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Shut down the VM?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The application and database will stop, and the server will power off about one minute later. The site
            will be unavailable until the VM is started again from the AWS console. Type <b>{phrase}</b> to confirm.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Confirmation"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            inputProps={{ "aria-label": "confirmation" }}
          />
          {submitError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submitError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void handleShutdown()}
            disabled={confirmText.trim() !== phrase || submitting}
          >
            {submitting ? "Shutting down…" : "Shut down"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default VmMaintenancePage;
